import React, { useEffect, useState } from "react";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { auth } from "../../firebaseconfig";
import "./PhoneAuth.css";

export default function PhoneAuth({ onAuthSuccess }) {
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showOtpBox, setShowOtpBox] = useState(false);
  const [verified, setVerified] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // ✅ Initialize reCAPTCHA safely
  useEffect(() => {
    if (window.recaptchaVerifier) return;

    // Delay helps avoid React 18 double-effect issues
    setTimeout(() => {
      try {
        window.recaptchaVerifier = new RecaptchaVerifier(
          "recaptcha-container",
          { size: "invisible" },
          auth
        );
        console.log("reCAPTCHA initialized ✅");
      } catch (err) {
        console.error("reCAPTCHA init failed:", err);
      }
    }, 500);
  }, []);

  // ✅ Timer countdown for resend
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  // ✅ Send OTP with reCAPTCHA readiness check
  const handleSendOtp = async () => {
    setError("");
    if (!phone || phone.length < 10) {
      setError("Please enter a valid 10-digit phone number.");
      return;
    }

    try {
      setLoading(true);
      const fullPhone = phone.startsWith("+91") ? phone : `+91${phone}`;

      // Ensure reCAPTCHA is initialized
      if (!window.recaptchaVerifier) {
        console.warn("reCAPTCHA not ready, initializing...");
        window.recaptchaVerifier = new RecaptchaVerifier(
          "recaptcha-container",
          { size: "invisible" },
          auth
        );
      }

      const appVerifier = window.recaptchaVerifier;
      await appVerifier.render(); // ✅ wait until reCAPTCHA is ready

      const confirmation = await signInWithPhoneNumber(
        auth,
        fullPhone,
        appVerifier
      );

      setConfirmationResult(confirmation);
      setShowOtpBox(true);
      setResendTimer(30); // 30s before allowing resend
      console.log("OTP sent to", fullPhone);
    } catch (err) {
      console.error("OTP send error:", err);
      setError(
        err.message ||
          "Failed to send OTP. Please check your number and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // ✅ Verify OTP
  const handleVerifyOtp = async () => {
    setError("");
    if (!otp || otp.length < 4) {
      setError("Please enter the OTP.");
      return;
    }

    try {
      setLoading(true);
      const result = await confirmationResult.confirm(otp);
      console.log("Verified user:", result.user);
      setVerified(true);
      setShowOtpBox(false);
      if (onAuthSuccess) onAuthSuccess(result.user);
    } catch (err) {
      console.error("OTP verify error:", err);
      setError("Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Resend OTP handler
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    setOtp("");
    await handleSendOtp();
  };

  return (
    <div className="phone-auth-container">
      <h3>Phone Authentication</h3>

      {/* Phone input */}
      <div className="phone-input-row">
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
          maxLength="10"
          placeholder="Enter 10-digit phone number"
          disabled={verified}
        />
        {phone.length === 10 && !showOtpBox && !verified && (
          <button
            onClick={handleSendOtp}
            disabled={loading}
            className="send-otp-btn"
          >
            {loading ? "Sending..." : "Send OTP"}
          </button>
        )}
      </div>

      {/* OTP input */}
      {showOtpBox && !verified && (
        <div className="otp-box">
          <input
            type="text"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            maxLength="6"
            placeholder="Enter OTP"
            className="otp-input"
          />
          <div className="otp-actions">
            <button
              onClick={handleVerifyOtp}
              disabled={loading}
              className="verify-btn"
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>

            <button
              onClick={handleResendOtp}
              disabled={resendTimer > 0 || loading}
              className="resend-btn"
            >
              {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend OTP"}
            </button>
          </div>
        </div>
      )}

      {/* Success / Error messages */}
      {verified && <p className="success-msg">✅ Phone verified successfully!</p>}
      {error && <p className="error-msg">{error}</p>}

      <div id="recaptcha-container"></div>
    </div>
  );
}
